const API_BASE_URL = '/api';

export interface EditableCustomer {
  dataverseCustomerId: string;
  repairShoprCustomerId: number | string;
  firstName: string;
  lastName: string;
  fullName: string;
  businessName: string;
  designation: string;
  email: string;
  phone: string;
  mobile: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
  getSms: boolean;
  optOut: boolean;
  disabled: boolean;
  noEmail: boolean;
  notificationEmail: string;
  invoiceCcEmails: string;
  alias: string;
  domains: string;
  abn: string;
  relationshipTerminationDate: string | null;
  sendXeroInvoiceOnly: boolean;
  xeroContactId: string;
  /** Billing-group parent (self-referential lookup). Null = standalone. */
  parentCustomerId: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export type EditableCustomerUpdates = Partial<Omit<EditableCustomer,
  'dataverseCustomerId' | 'repairShoprCustomerId' | 'createdAt' | 'updatedAt'
>>;

export interface CustomerUpdateResult {
  customer: EditableCustomer | null;
  dataverse_updated: boolean;
  jobs_cascaded: number;
  repairshopr_synced: boolean;
  repairshopr_error: string | null;
}

async function parseError(response: Response, fallback: string): Promise<Error> {
  const data = await response.json().catch(() => ({ error: `${fallback}: ${response.status}` }));
  return new Error(data.error || fallback);
}

export async function getCustomerForEdit(
  customerId: string,
  signal?: AbortSignal,
): Promise<EditableCustomer> {
  const response = await fetch(
    `${API_BASE_URL}/customers/${customerId}/edit`,
    { method: 'GET', headers: { Accept: 'application/json' }, signal },
  );

  if (!response.ok) {
    throw await parseError(response, 'Failed to fetch customer for edit');
  }

  return response.json();
}

export async function updateCustomer(
  customerId: string,
  updates: EditableCustomerUpdates,
  signal?: AbortSignal,
): Promise<CustomerUpdateResult> {
  const response = await fetch(
    `${API_BASE_URL}/customers/${customerId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
      signal,
    },
  );

  if (!response.ok) {
    throw await parseError(response, 'Failed to update customer');
  }

  return response.json();
}
